import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

export default function Preorder() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);

    // Honeypot check
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      setEmail("");
      formEl.reset();
      return;
    }
    fd.delete("website_alt");

    setStatus("loading");
    setErrorMsg("");

    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, String(v)));

      const res = await fetch("https://readdy.ai/api/form/d9fjc9mlb8e5uktkmtlg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || text || "";
      const isSpamMsg = /spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpamMsg) {
        setStatus("success");
        setEmail("");
        formEl.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  return (
    <section
      id="preorder"
      className="w-full px-6 md:px-10 py-24 md:py-32 border-t border-background-200"
    >
      {/* Section header row */}
      <Reveal y={30}>
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            <span>05 — Reserve</span>
          </p>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
            <span>Two hundred, then quiet</span>
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-stretch">
        {/* LEFT — Media block */}
        <Reveal y={60} duration={1000}>
          <div className="relative w-full h-full min-h-[520px] rounded-lg overflow-hidden bg-background-100 border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Minimalist%20still%20life%20of%20a%20matte%20black%20AI%20device%20carefully%20placed%20inside%20a%20folded%20warm%20off%20white%20paper%20package%20with%20a%20visible%20serial%20number%20tag%2C%20soft%20side%20light%20from%20left%2C%20long%20soft%20shadow%2C%20muted%20monochrome%20warm%20gray%20background%2C%20editorial%20fine%20art%20product%20photography%2C%20refined%20negative%20space%2C%20high%20end%20unboxing%20detail&width=1200&height=1500&seq=topbud-preorder-main&orientation=portrait"
              alt="First batch, numbered"
              className="w-full h-full object-cover object-top"
            />
            {/* Top-left tag */}
            <div className="absolute left-4 top-4 flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
              <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                First Batch / 2025
              </span>
            </div>
            {/* Bottom-left number */}
            <div className="absolute left-6 bottom-6">
              <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                Edition of
              </p>
              <p className="mt-2 font-heading font-semibold text-[72px] leading-none tracking-[-0.03em] text-background-50">
                200
              </p>
              <p className="mt-3 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                Numbered · Autumn 2025
              </p>
            </div>
            {/* Bottom-right serial */}
            <span className="absolute right-4 bottom-4 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/70">
              SN 0042 / 0200
            </span>
          </div>
        </Reveal>

        {/* RIGHT — Copy + form */}
        <div className="flex flex-col justify-between">
          <div className="max-w-md">
            <Reveal delay={120} y={40}>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                <span>Pre-Order</span>
              </p>
            </Reveal>
            <Reveal delay={220} y={40}>
              <h2 className="mt-6 font-heading font-semibold text-[48px] md:text-[68px] leading-[0.95] tracking-[-0.03em] text-foreground-950">
                First <br />
                <span className="italic font-normal text-foreground-700">
                  batch.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={340} y={40}>
              <p className="mt-8 text-[13px] leading-[1.75] text-foreground-700 text-justify">
                Two hundred units. Each numbered by hand, wrapped in warm paper,
                and shipped through autumn. Leave your email and we&apos;ll
                write once — quietly — only when yours is ready to leave the
                studio.
              </p>
            </Reveal>

            {/* Meta grid */}
            <Reveal delay={460} y={30}>
              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-background-200 pt-6">
                {[
                  { k: "Edition", v: "200" },
                  { k: "Price", v: "$ 489" },
                  { k: "Ships", v: "Oct 25" },
                ].map((m) => (
                  <div key={m.k}>
                    <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                      {m.k}
                    </p>
                    <p className="mt-2 font-heading text-[22px] leading-none tracking-[-0.02em] text-foreground-950">
                      {m.v}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={560} y={40} className="w-full mt-12">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="topbud-newsletter-form"
              className="w-full"
              noValidate
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field-alt"
              />

              <div className="flex items-center justify-between">
                <label
                  htmlFor="preorder-email"
                  className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                >
                  Reserve · Email
                </label>
                <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-400">
                  Step 01 / 01
                </span>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-3 border-b border-foreground-300 pb-2">
                <input
                  id="preorder-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@studio.com"
                  className="flex-1 bg-transparent outline-none text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-[11px] font-label tracking-[0.18em] uppercase hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60"
                >
                  <span>{status === "loading" ? "Sending" : "Reserve"}</span>
                  <i className="ri-arrow-right-up-line text-[13px]" />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-between min-h-[16px]">
                <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                  One email. Only when yours is ready.
                </p>
                {status === "success" && (
                  <p className="font-label text-[11px] tracking-[0.18em] uppercase text-accent-600">
                    <span>Thank you — we&apos;ll be in touch.</span>
                  </p>
                )}
                {status === "error" && (
                  <p className="font-label text-[11px] tracking-[0.18em] uppercase text-accent-700">
                    <span>{errorMsg}</span>
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}